import { ITooltipParams } from 'ag-grid-enterprise';
import { format, isAfter, isBefore, isValid, parse, parseISO, subDays } from 'date-fns';
import {
  fpxDocType,
  htmlDocType,
  jsonDocType,
  msgDocType,
  pxtDocType,
  txtDocType,
  xmlDocType,
  zipDocType,
} from 'features/constants';
import * as _ from 'lodash';
import moment from 'moment';
import { propertyType } from '../api/file/interfaces/get-file';
import { IGetLenderResponse } from '../features/files/parties/interfaces/CreateLenderRequest';
import { SelectOption } from '../ui-kit/inputs/AgentNetDropdownSelector';

/**
 * Handles key press events for input fields.
 * Calls the callback on Enter key, and restricts input to numbers and dashes if zipcodeEnabled is true.
 * @param e - The keyboard event.
 * @param callback - Function to call on Enter key.
 * @param zipcodeEnabled - If true, restricts input to numbers and dashes.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const keyPress = (
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  callback: () => void,
  zipcodeEnabled?: boolean,
) => {
  if (e.key === 'Enter') {
    callback();
    e.preventDefault();
    return false;
  }

  if (zipcodeEnabled) {
    const regex = new RegExp('^[0-9-]+$');
    if (regex.test(e.key) || e.key === 'Backspace') return true;
    e.preventDefault();
    return false;
  }
};

/**
 * Formats a date value from ag-grid tooltip params to MM/DD/YYYY.
 * @param params - Tooltip parameters containing the value.
 * @returns Formatted date string.
 */
export const dateTooltipValue = (params: ITooltipParams) => {
  const val = moment(params.value).format('MM/DD/YYYY');
  return val;
};

/**
 * Replaces '+' with ',' in the error tooltip value.
 * @param params - Tooltip parameters containing the value.
 * @returns Modified string.
 */
export const errorTooltipValue = (params: ITooltipParams) => {
  const val = params?.value?.replace('+', ',');
  return val;
};

/**
 * Checks if a date is valid and after 1/1/1000.
 * @param date - The date to validate.
 * @returns True if valid, false otherwise.
 */
export const isValidDate = (date: Date | null): boolean | null => {
  return date && isValid(date) && isAfter(date, new Date('1/1/1000'));
};

const FORMATS = ['MM/dd/yy', 'M/dd/yy', 'M/dd', 'MM/dd', 'M/d', 'MM/d', 'M'];
/**
 * Attempts to parse a date string using the provided format and fallback formats.
 * @param str - The date string.
 * @param format - The primary format to use.
 * @returns Parsed Date object or null.
 */
export const parseDate = (str: string, format: string): Date | null => {
  let parsedDate: Date | null = null;
  if (!str.length) {
    return null;
  }

  parsedDate = parse(str, format, new Date());
  if (!isValidDate(parsedDate)) {
    for (let i = 0; i < FORMATS.length; i++) {
      parsedDate = parse(str, FORMATS[i], new Date());
      if (isValidDate(parsedDate)) {
        return parsedDate;
      }
    }

    if (!isValidDate(parsedDate)) {
      const newDate = new Date(str);
      parsedDate = isValidDate(newDate) ? newDate : parsedDate;
    }
  }
  return parsedDate;
};

/**
 * Gets a list of SelectOptions for dropdowns from a given enum.
 * @param enumClass - The enum object.
 * @returns Array of SelectOption objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
export function getSelectOptions(enumClass: any): SelectOption[] {
  return Object.values(enumClass).map((enumVal) => {
    const enumKey = Object.keys(enumClass)[Object.values(enumClass).indexOf(enumVal)];
    const selectOption: SelectOption = { name: enumVal as string, value: enumKey };
    return selectOption;
  });
}

/**
 * Returns the loan amount as a number if lender and loanAmount exist, otherwise 0.
 * @param lender - Lender object.
 * @returns Loan amount as number.
 */
export const checkNumberOfLenderType = (lender?: IGetLenderResponse): number => {
  if (lender && lender.loanAmount) {
    return +lender.loanAmount;
  } else {
    return 0;
  }
};

/**
 * Checks if an object is empty (has no own properties).
 * @param obj - The object to check.
 * @returns True if empty, false otherwise.
 */
export const isObjectEmpty = <T extends Record<string, unknown>>(obj: T): boolean => {
  return Object.keys(obj).length === 0;
};

/**
 * Opens a document from a base64 string in a new window or triggers download based on type.
 * @param pdf - Base64 string of the document.
 * @param documentType - MIME type of the document.
 * @param windowName - Name of the window.
 * @param windowFeatures - Features for the window.
 * @param fileName - Optional file name for download.
 */
export const openDocument = (
  pdf: string,
  documentType: string,
  windowName: string,
  windowFeatures: string,
  fileName = '',
) => {
  const byteCharacters = atob(pdf);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const file = new Blob([byteArray], { type: documentType });

  handleBlob(file, documentType, windowName, windowFeatures, fileName ? fileName : 'document');
};

const createDownloadLink = (fileURL: string, documentType: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = fileURL;
  const extensionMap: { [key: string]: string } = {
    [htmlDocType]: 'html',
    [xmlDocType]: 'xml',
    [txtDocType]: 'txt',
    [msgDocType]: 'msg',
    [jsonDocType]: 'json',
    [zipDocType]: 'zip',
    [fpxDocType]: 'fpx',
    [pxtDocType]: 'pxt',
  };
  const extension = extensionMap[documentType] || documentType.split('/').pop();
  link.setAttribute('download', `${fileName}.${extension}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(fileURL);
};

const handleBlob = (file: Blob, documentType: string, windowName: string, windowFeatures: string, fileName: string) => {
  const fileURL = URL.createObjectURL(file);
  if (
    [htmlDocType, xmlDocType, txtDocType, msgDocType, jsonDocType, zipDocType, pxtDocType, fpxDocType].includes(
      documentType,
    )
  ) {
    createDownloadLink(fileURL, documentType, fileName);
  } else {
    window.open(fileURL, windowName, windowFeatures);
  }
};

const handleURL = (
  fileUrl: string,
  documentType: string,
  windowName: string,
  windowFeatures: string,
  fileName: string,
) => {
  const fileURL = fileUrl;
  if ([htmlDocType, xmlDocType, txtDocType, msgDocType, jsonDocType, zipDocType].includes(documentType)) {
    createDownloadLink(fileURL, documentType, fileName);
  } else {
    window.open(fileURL, windowName, windowFeatures);
  }
};

/**
 * Triggers a file download for a given Blob object or opens it in a new window based on type.
 * @param fileBlob - The Blob object representing the file.
 * @param documentType - MIME type of the document.
 * @param windowName - Name of the window.
 * @param windowFeatures - Features for the window.
 * @param fileName - Name of the file to be downloaded.
 */
export const openBlobDocument = (
  fileBlob: Blob,
  documentType: string,
  windowName: string,
  windowFeatures: string,
  fileName: string,
): void => {
  handleBlob(fileBlob, documentType, windowName, windowFeatures, fileName);
};

/**
 * Triggers a file download for a given file URL or opens it in a new window based on type.
 * @param fileUrl - The URL of the file.
 * @param documentType - MIME type of the document.
 * @param windowName - Name of the window.
 * @param windowFeatures - Features for the window.
 * @param fileName - Name of the file to be downloaded.
 */
export const openURLDocument = (
  fileUrl: string,
  documentType: string,
  windowName: string,
  windowFeatures: string,
  fileName: string,
): void => {
  handleURL(fileUrl, documentType, windowName, windowFeatures, fileName);
};

/**
 * Opens a PDF response in a new browser tab.
 * @param pdfRes - The fetch Response object containing the PDF.
 * @param fileName - The name for the PDF file.
 */
export async function openPdfResponse(pdfRes: Response, fileName: string) {
  const fileBlob = await pdfRes.blob();
  window.open(window.URL.createObjectURL(fileBlob), '_blank');
}

/**
 * Returns an array of formatted addresses for Google Maps from a list of properties.
 * @param properties - Array of property objects.
 * @returns Array of address strings.
 */
export function getMapAddressList(properties: Array<propertyType>): string[] {
  if (!properties) return [];
  const addressList: string[] = properties.map((property) => {
    const { address1, city, state, postalCode } = property;
    let address = address1 || '';
    if (address) address += ', ';
    address += city || '';
    if (city) address += ' ';
    address += state || '';
    if (state) address += ' ';
    address += postalCode || '';
    return address;
  });
  return addressList.filter((address) => address !== '');
}

export interface iErrorData {
  path: string;
  errors: string[];
}

/**
 * Converts an array of iErrorData to a single string of error messages.
 * @param data - Array of iErrorData or a string.
 * @returns Concatenated error messages as a string.
 */
export function errorDataToString(data: iErrorData[]): string {
  // If the data is a string, just return it
  if (typeof data === 'string') return data;

  // Handle array of iErrorData
  let errString = '';
  for (const errData of data) {
    for (const errMessage of errData.errors) {
      if (errString !== '') errString += ', ';
      errString += errMessage;
    }
  }
  return errString;
}

/**
 * Returns a unique array of strings.
 * @param err - Array of strings.
 * @returns Array with unique values.
 */
export const uniqueArray = (err: string[]) => Array.from(new Set([...err]));

/**
 * Formats a number as a dollar amount with commas.
 * @param x - The number to format.
 * @returns Formatted string with dollar sign and commas.
 */
export function getAmountWithCommas(x: any): string {
  return `$${x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

/**
 * Throws an error if the data contains a statusCode or errorCode.
 * @param data - The error data object.
 */
export const handleError = (data: any) => {
  // Portal APIs
  if (data.statusCode || data.errorCode) {
    throw { messages: data.messages, errorCode: data.statusCode || data.errorCode, data: data };
  }
  // Agency APIs
  if (data.Errors?.length) {
    throw Error(data.Errors[0].Message);
  }
};

export interface ApiErrorPayload {
  messages?: string[];
  message?: string;
  errorCode?: number | string;
}

/**
 * Extracts displayable messages from an error thrown by handleError or handleBusinessError.
 * Portal APIs throw a messages array, Agency APIs throw an Error with a single message.
 * @param error - The caught error.
 * @returns The error messages to display.
 */
export const getApiErrorMessages = (error: unknown): string[] => {
  const payload = (error ?? {}) as ApiErrorPayload;
  return payload.messages ?? [payload.message ?? String(error)];
};

/**
 * Throws an error if the data contains business errors.
 * @param data - The error data object.
 */
export const handleBusinessError = (data: any) => {
  if (data.Errors && data.Errors.length > 0) {
    const errMessages = [];
    for (const errData of data.Errors) {
      const message = errData.Message;
      errMessages.push(message);
    }
    throw { messages: errMessages, errorCode: data.Errors[0].ErrorCode, data: data };
  }
};

type JsonValue = string | number | boolean | null | undefined | JsonObject;
interface JsonObject {
  [key: string]: JsonValue;
}

const toBeIgnored = ['isSdnMatched', 'region', 'uuid'];
/**
 * Recursively removes empty values and specified keys from an object or array.
 * @param obj - The object or array to clean.
 * @returns Cleaned object or array.
 */
export const removeEmpty = (obj: any): JsonValue => {
  if (_.isPlainObject(obj)) {
    return _.transform(obj as any, (result: JsonObject, value: JsonValue, key: string) => {
      const cleanedValue = removeEmpty(value);
      if (!_.isNil(cleanedValue) && cleanedValue !== '' && !toBeIgnored.includes(key)) {
        result[key] = cleanedValue;
      }
    });
  } else if (Array.isArray(obj)) {
    return _.compact(_.map(obj, removeEmpty)) as any;
  } else {
    return obj;
  }
};

/**
 * Formats a number as a dollar amount with two decimal places and commas.
 * @param str - The number to format.
 * @returns Formatted dollar string.
 */
export const dollarize = (str: number) => '$' + str.toLocaleString('en-US', { minimumFractionDigits: 2 });

/**
 * List of product statuses for which fields should be hidden.
 */
export const hideFieldsWithStatus = ['Pending Remit', 'Remitted'];

/**
 * Calculates gross and net totals for a list of products, considering remitted status.
 * @param products - Array of product objects.
 * @param hasRemittedProduct - Whether there is a remitted product.
 * @returns Object with gross and net totals.
 */
export const calculateTotals = (products: any, hasRemittedProduct: boolean) => {
  let grossTotal = 0;
  let netTotal = 0;

  products.forEach((prod: any) => {
    const skipCalcuation = !hasRemittedProduct && hideFieldsWithStatus.includes(prod.status);
    prod.grossReported && !skipCalcuation && (grossTotal += prod.grossReported);
    prod.netReported && !skipCalcuation && (netTotal += prod.netReported);

    if (prod.additionalFees && prod.additionalFees.length) {
      prod.additionalFees.forEach((ch: any) => {
        const skipCHCalculation = !hasRemittedProduct && hideFieldsWithStatus.includes(ch.status);
        ch.grossReported && !skipCHCalculation && (grossTotal += ch.grossReported);
        ch.netReported && !skipCHCalculation && (netTotal += ch.netReported);
        if (ch.additionalFees && ch.additionalFees.length) {
          ch.additionalFees.forEach((zch: any) => {
            const skipZCHCalculation = !hasRemittedProduct && hideFieldsWithStatus.includes(zch.status);
            zch.grossReported && !skipZCHCalculation && (grossTotal += zch.grossReported);
            zch.netReported && !skipZCHCalculation && (netTotal += zch.netReported);
          });
        }
      });
    }
  });

  return { gross: grossTotal, net: netTotal };
};

/**
 * Calculates gross and net totals for policy products with 'Jacket Premium' rateFeeType.
 * @param products - Array of product objects.
 * @returns Object with gross and net totals.
 */
export const policyCalculateTotals = (products: any) => {
  let gross = 0;
  let net = 0;
  products.forEach((prod: any) => {
    if (prod.rateFeeType === 'Jacket Premium') {
      prod.grossReported && (gross += prod.grossReported);
      prod.netReported && (net += prod.netReported);
    }
  });

  return { gross, net };
};

/**
 * Converts a date period string to a date range object with Date objects.
 * @param period - The period string (e.g., 'today', 'last7Days').
 * @returns Object with 'from' and 'to' Date objects.
 */
export const getDateRangeFromPeriod = (period?: string): { from: Date; to: Date } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let from: Date;
  let to: Date = new Date(today);

  switch (period) {
    case 'today':
      from = new Date(today);
      break;
    case 'yesterday':
      from = new Date(today);
      from.setDate(today.getDate() - 1);
      to = new Date(from);
      break;
    case 'thisWeek':
      from = new Date(today);
      from.setDate(today.getDate() - today.getDay());
      break;
    case 'lastWeek':
      to = new Date(today);
      to.setDate(today.getDate() - today.getDay() - 1);
      from = new Date(to);
      from.setDate(to.getDate() - 6);
      break;
    case 'thisMonth':
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'lastMonth':
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'last7Days':
      from = new Date(today);
      from.setDate(today.getDate() - 6);
      break;
    case 'last30Days':
      from = new Date(today);
      from.setDate(today.getDate() - 29);
      break;
    case 'last90Days':
      from = new Date(today);
      from.setDate(today.getDate() - 89);
      break;
    default: // today
      from = new Date(today);
      break;
  }

  return { from, to };
};

/**
 * Converts a date period string to a date range object with 'from' and 'to' in yyyy-MM-dd format.
 * @param period - The period string (e.g., 'today', 'last7Days').
 * @returns Object with 'from' and 'to' date strings.
 */
export const convertDatePeriodToDateRange = (period?: string): { from: string; to: string } => {
  const today = new Date();
  let from: Date;
  let to: Date = today;

  switch (period) {
    case 'today':
      from = to;
      break;
    case 'yesterday':
      from = new Date(today);
      from.setDate(today.getDate() - 1);
      to = from;
      break;
    case 'thisWeek':
      from = new Date(today);
      from.setDate(today.getDate() - today.getDay());
      to = new Date(today);
      to.setDate(from.getDate() + 6);
      break;
    case 'lastWeek':
      from = new Date(today);
      from.setDate(today.getDate() - today.getDay() - 7);
      to = new Date(from);
      to.setDate(from.getDate() + 6);
      break;
    case 'thisMonth':
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'lastMonth':
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'last7Days':
      from = new Date(today);
      from.setDate(today.getDate() - 7);
      break;
    case 'last30Days':
      from = new Date(today);
      from.setDate(today.getDate() - 30);
      break;
    case 'last90Days':
      from = new Date(today);
      from.setDate(today.getDate() - 90);
      break;
    default: // today
      from = to;
      break;
  }

  const formatDate = (date: Date) =>
    `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;

  return { from: formatDate(from), to: formatDate(to) };
};

/**
 * Formats a date string to 'MM/dd/yyyy at hh:mm am/pm' in lowercase.
 * @param dateString - The date string to format.
 * @returns Formatted date-time string.
 */
export const formatDateTime = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  return format(new Date(dateString), `MM/dd/yyyy 'at' hh:mm a`).toLowerCase();
};

export const normalizeServiceName = (name: string, separator = '-'): string => {
  return name.toLowerCase().replace(/\s+/g, separator);
};

/**
 * Returns a two-letter name tag from a full name (first and last initials).
 * @param fullName - The full name string.
 * @returns Name tag string.
 */
export const formatNameTag = (fullName: string): string => {
  const names = fullName
    .trim()
    .split(/\s+/)
    .map((name) => name.trim());
  const firstName = names[0] ? names[0][0].toUpperCase() : '';
  const lastName = names.length > 1 ? names[names.length - 1][0].toUpperCase() : '';
  return `${firstName}${lastName}`;
};

/**
 * Returns the full name from a user object, including middle name if present.
 * @param user - Object with firstName, middleName, and lastName.
 * @returns Full name string.
 */
export const getFullName = (user: { firstName?: string; middleName?: string; lastName?: string }): string => {
  const { firstName, middleName, lastName } = user;
  if (firstName && middleName && lastName) {
    return `${firstName} ${middleName} ${lastName}`.trim();
  }
  if (firstName && lastName) {
    return `${firstName} ${lastName}`.trim();
  }
  return firstName?.trim() || lastName?.trim() || '';
};

// Recursively convert all object keys to camelCase
export function normalizeKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(normalizeKeysToCamelCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[_.camelCase(key)] = normalizeKeysToCamelCase(value);
      return acc;
    }, {} as Record<string, any>);
  }
  return obj;
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

export function isEmailOlderThanSixMonths(emailRegisteredDate?: string): boolean {
  if (!emailRegisteredDate) return false;
  const registered = parseISO(emailRegisteredDate);
  if (!isValid(registered)) return false;
  const sixMonthsAgo = subDays(new Date(), 180);
  return isBefore(registered, sixMonthsAgo);
}

export function formatCurrency(amount: number | undefined | null): string {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export function formatFullAddress(address?: string, city?: string, stateCode?: string, postalCode?: string): string {
  const parts = [address, city, stateCode, postalCode].filter((part) => part?.trim()).map((part) => part?.trim());

  return parts.length > 0 ? parts.join(', ') : '-';
}

/**
 * Generates a cache-busting version string based on current date and time.
 * Format: YY.MM.DD.HHMM (e.g., 25.10.08.1430 for Oct 8, 2025 at 2:30 PM)
 * @returns Version string for cache busting
 */
export function getCacheBustingVersion(): string {
  const now = new Date();
  const year = String(now.getFullYear()).slice(-2); // Last 2 digits of year
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');

  return `${year}.${month}.${day}.${hour}${minute}`;
}

export function makeRandomId(): string {
  return (Math.random() + 1).toString(36).substring(7);
}

/**
 * Sanitizes a filename to make it safe for downloading across different operating systems
 * @param fileName - The original filename
 * @returns A sanitized filename safe for downloads
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return 'document';

  return (
    fileName
      .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid filename characters with underscore
      .replace(/\s+/g, '_') // Replace spaces with underscaces
      .replace(/_{2,}/g, '_') // Replace multiple consecutive underscores with single underscore
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      .substring(0, 200) || // Limit length to avoid filesystem issues
    'document' // Fallback if result is empty
  );
};

/**
 * Sanitizes a URL to prevent XSS attacks in iframe src and img src attributes.
 * Only allows HTTPS URLs and localhost HTTP URLs (for development).
 * Returns 'about:blank' or empty string for invalid URLs.
 *
 * @param url - The URL to sanitize
 * @param allowDataUri - Whether to allow data: URIs (default: false)
 * @param fallback - Fallback value for invalid URLs (default: 'about:blank')
 * @returns Sanitized URL or fallback value
 *
 * @example
 * sanitizeUrl('https://example.com/api') // returns 'https://example.com/api'
 * sanitizeUrl('javascript:alert(1)') // returns 'about:blank'
 * sanitizeUrl('http://example.com') // returns 'about:blank' (HTTP not allowed except localhost)
 * sanitizeUrl('http://localhost:3000/api') // returns 'http://localhost:3000/api'
 * sanitizeUrl(null) // returns 'about:blank'
 */
export const sanitizeUrl = (url: string | null | undefined, allowDataUri = false, fallback = 'about:blank'): string => {
  // Return fallback for null, undefined, or empty strings
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return fallback;
  }

  const trimmedUrl = url.trim();

  try {
    // Check for dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
    const lowercaseUrl = trimmedUrl.toLowerCase();

    // Block dangerous protocols (except data: if explicitly allowed)
    for (const protocol of dangerousProtocols) {
      if (protocol === 'data:' && allowDataUri) continue;
      if (lowercaseUrl.startsWith(protocol)) {
        return fallback;
      }
    }

    // Allow HTTPS URLs
    if (trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }

    // Allow HTTP only for localhost (development)
    if (trimmedUrl.startsWith('http://')) {
      const urlObj = new URL(trimmedUrl);
      if (urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1') {
        return trimmedUrl;
      }
      // Reject non-localhost HTTP URLs
      return fallback;
    }

    // Allow relative URLs (starting with /)
    if (trimmedUrl.startsWith('/')) {
      return trimmedUrl;
    }

    // Reject everything else
    return fallback;
  } catch (error) {
    // Invalid URL format
    return fallback;
  }
};

/**
 * Sanitizes HTML content to prevent XSS attacks when using dangerouslySetInnerHTML.
 * Removes script tags, dangerous event handlers, and javascript: URLs.
 * This is a basic sanitizer for trusted backend content - for untrusted user content, use DOMPurify library.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * sanitizeHtml('<p>Hello</p>') // returns '<p>Hello</p>'
 * sanitizeHtml('<script>alert(1)</script>') // returns ''
 * sanitizeHtml('<p onclick="alert(1)">Click</p>') // returns '<p>Click</p>'
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Remove script tags and their content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove potentially dangerous tags
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '');
  sanitized = sanitized.replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
  sanitized = sanitized.replace(/<applet\b[^<]*(?:(?!<\/applet>)<[^<]*)*<\/applet>/gi, '');

  // Remove event handlers (onclick, onerror, onload, etc.)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*[^\s>]*/gi, '');

  // Remove javascript: URLs
  sanitized = sanitized.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  sanitized = sanitized.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""');

  return sanitized;
};
